import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import type { ProcesoProveedor } from '../../../types/collections/ProcesoProveedor';

interface ProcesoProveedorModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (data: Partial<ProcesoProveedor>) => Promise<void>;
  proveedor?: ProcesoProveedor | null;
  title: string;
}

// Simple email regex for client-side validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ProcesoProveedorModal: React.FC<ProcesoProveedorModalProps> = ({
  isOpen,
  toggle,
  onSave,
  proveedor,
  title
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    direccion: '',
    nit: ''
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Inicializar formulario
  useEffect(() => {
    if (proveedor) {
      setFormData({
        nombre: proveedor.nombre || '',
        correo: proveedor.correo || '',
        telefono: proveedor.telefono ? proveedor.telefono.toString() : '',
        direccion: proveedor.direccion || '',
        nit: proveedor.nit || ''
      });
    } else {
      setFormData({
        nombre: '',
        correo: '',
        telefono: '',
        direccion: '',
        nit: ''
      });
    }
    setLogoFile(null);
    setErrors({});
    setSaveError(null);
  }, [proveedor, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (formData.correo && !EMAIL_REGEX.test(formData.correo)) {
      newErrors.correo = 'El formato del correo no es válido';
    }

    if (formData.telefono && !/^\d+$/.test(formData.telefono)) {
      newErrors.telefono = 'El teléfono debe contener solo números';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSaveError(null);

    try {
      const saveData: Partial<ProcesoProveedor> = {
        ...formData,
        telefono: formData.telefono ? parseInt(formData.telefono, 10) : null,
        activo: true
      };

      // Si hay un archivo de logo, agregarlo a los datos
      if (logoFile) {
        saveData.logo = logoFile;
      }

      await onSave(saveData);
      toggle();
    } catch (error) {
      setSaveError('Error al guardar el proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {saveError && <Alert color="danger" timeout={5000}>{saveError}</Alert>}
          
          <FormGroup>
            <Label for="nombre">Nombre *</Label>
            <Input
              id="nombre"
              type="text"
              value={formData.nombre}
              onChange={(e) => handleInputChange('nombre', e.target.value)}
              invalid={!!errors.nombre}
            />
            {errors.nombre && <div className="invalid-feedback">{errors.nombre}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="nit">NIT</Label>
            <Input
              id="nit"
              type="text"
              value={formData.nit}
              onChange={(e) => handleInputChange('nit', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label for="correo">Correo</Label>
            <Input
              id="correo"
              type="email"
              value={formData.correo}
              onChange={(e) => handleInputChange('correo', e.target.value)}
              invalid={!!errors.correo}
            />
            {errors.correo && <div className="invalid-feedback">{errors.correo}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="telefono">Teléfono</Label>
            <Input
              id="telefono"
              type="tel"
              value={formData.telefono}
              onChange={(e) => handleInputChange('telefono', e.target.value)}
              invalid={!!errors.telefono}
            />
            {errors.telefono && <div className="invalid-feedback">{errors.telefono}</div>}
          </FormGroup>

          <FormGroup>
            <Label for="direccion">Dirección</Label>
            <Input
              id="direccion"
              type="text"
              value={formData.direccion}
              onChange={(e) => handleInputChange('direccion', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label for="logo">Logo</Label>
            <Input
              id="logo"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
            />
            {logoFile && (
              <small className="text-muted">
                Archivo seleccionado: {logoFile.name}
              </small>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ProcesoProveedorModal;